import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import Modal from 'react-modal'
import csv from 'csvtojson'
import { Alert } from '@wellbeingxltd/fe-lib/components'
import { GET_STORE } from '@wellbeingxltd/fe-lib/constants'
import { formatPrice } from '@wellbeingxltd/fe-lib/helpers'
import AnimatedLogo from '@wellbeingxltd/fe-lib/images/dist/logo/AnimatedLogo.gif'
import { TextPill } from 'components'
import { VouchersActions } from 'store/models/vouchers'
import SupplierActions from 'store/models/supplier'
import styles from './VouchersTable.module.scss'
import { UploadNewVouchers } from './UploadNewVouchers'

// The same component as on W. TODO move to fe-lib
const ClickableTextPill = ({ voucher: { totalReceived, totalUsed } }) => {
    const value = `${totalReceived - totalUsed} of ${totalReceived}`
    const title = totalReceived - totalUsed < 10 ? 'There are <10 vouchers remaining.' : null
    const color = totalReceived - totalUsed < 10 ? 'red' : 'green'

    return <TextPill {...{ color, title, value }} />
}

const Row = ({ index, setIsModalOpen, setNewVoucherSupplierData, supplierId, voucher }) => (
    <div className={styles.row} key={index}>
        <button
            className={styles.addNewVouchersBtn}
            onClick={() => {
                setIsModalOpen(true)
                setNewVoucherSupplierData({
                    price: voucher.price,
                    priceDesc: voucher.priceDesc,
                    productId: voucher.productId,
                    resourceId: voucher.resourceId,
                    resourceTitle: voucher.resourceTitle,
                    supplierId,
                })
            }}
        >
            +
        </button>
        <span>
            {voucher.resourceTitle} - {voucher.priceDesc}
        </span>
        <span>{formatPrice(voucher.price)}</span>
        <ClickableTextPill {...{ voucher }} />
        <span>{voucher.productId}</span>
    </div>
)

const toFindDuplicates = array => {
    const codes = array.map(({ code }) => code)
    const uniqueElements = new Set(codes)
    const filteredElements = codes.filter(item => {
        if (uniqueElements.has(item)) {
            uniqueElements.delete(item)
        } else {
            return item
        }
    })

    return [...new Set(filteredElements)]
}

export const VouchersTable = () => {
    const dispatch = useDispatch()
    const { getVouchersError, getVouchersLoading, vouchers } = useSelector(GET_STORE.VOUCHERS)
    const {
        supplier: { supplierId },
    } = useSelector(GET_STORE.SUPPLIER)

    const [isModalOpen, setIsModalOpen] = useState(false)
    const [newVoucherSupplierData, setNewVoucherSupplierData] = useState({})
    const [uploadedCsvData, setUploadedCsvData] = useState([])

    useEffect(() => {
        if (!supplierId) {
            dispatch(SupplierActions.getSupplier())
        }
    }, [])

    useEffect(() => {
        // Show error message if csv has duplicate codes
        const duplicates = toFindDuplicates(uploadedCsvData)
        if (duplicates.length) {
            setIsModalOpen(false)
            Alert(`You have duplicate codes: ${duplicates.join(', ')}. Please check your CSV file.`, 'info')
            setUploadedCsvData([])
            return
        }
    }, [uploadedCsvData])

    const handleFileUpload = ({ e, callback }) => {
        const file = e.target.files[0]
        const reader = new FileReader()
        reader.onload = evt => {
            csv({ noheader: true, output: 'line', trim: true })
                .fromString(evt.target.result)
                .then(arrayOfCodes => {
                    setUploadedCsvData(
                        arrayOfCodes
                            .filter(code => code)
                            .map(code => ({
                                code,
                                description: newVoucherSupplierData.priceDesc,
                                price: newVoucherSupplierData.price,
                            }))
                    )
                })
            callback()
        }
        reader.readAsBinaryString(file)
    }

    const closeModalAndClearVoucherData = () => {
        setIsModalOpen(false)
        setNewVoucherSupplierData({})
        setUploadedCsvData([])
    }

    const addNewVouchers = () => {
        const body = { ...newVoucherSupplierData, vouchers: uploadedCsvData }
        dispatch(VouchersActions.uploadVouchers(body, (msg, type) => Alert(msg, type)))

        closeModalAndClearVoucherData()
    }

    const columns = React.useMemo(
        () => [
            { Header: 'Code', accessor: 'code' },
            { Header: 'Description', accessor: 'description' },
            { Header: 'Price', accessor: 'price', Cell: ({ value }) => formatPrice(value) },
        ],
        []
    )

    return getVouchersLoading ? (
        <div className={styles.loadingBlock}>
            <img src={AnimatedLogo} alt="Loading spinner" />
        </div>
    ) : getVouchersError ? (
        <p>{getVouchersError}</p>
    ) : !vouchers.length ? (
        <p>No vouchers have been uploaded yet.</p>
    ) : (
        <>
            <div className={styles.ProductTable}>
                <div className={styles.row}>
                    <span className={styles.header}>Upload</span>
                    <span className={styles.header}>Name</span>
                    <span className={styles.header}>Price</span>
                    <span className={styles.header}>Remaining</span>
                    <span className={styles.header}>Product ID</span>
                </div>
                {vouchers.map((voucher, index) => (
                    <Row key={index} {...{ index, setIsModalOpen, setNewVoucherSupplierData, supplierId, voucher }} />
                ))}
            </div>

            <Modal
                appElement={document.getElementById('root')}
                onRequestClose={closeModalAndClearVoucherData}
                style={{
                    overlay: { backgroundColor: 'rgba(0,0,0,.8)', zIndex: 9999999 },
                    content: { border: 'none', borderRadius: '16px', inset: '20% 32px 20%', padding: '0' },
                }}
                isOpen={isModalOpen}
            >
                <UploadNewVouchers {...{ addNewVouchers, columns, handleFileUpload, setIsModalOpen, setUploadedCsvData, uploadedCsvData }} />
            </Modal>
        </>
    )
}
